<i18n>
{
  "en": {
    "saleRateIndexingRule": "Index sale rates",
    "addRule": "Add sale rate indexing rule",
    "deleteTitle": "Are you sure you want to delete this sale rate indexing rule?",
    "deleteError": "There was an error deleting the sale rate indexing rule",
    "createError": "There was an error creating the sale rate indexing rule",
    "noRules": "No sale rates available",
    "duplicateError": "The changed sale rate already has a rule applied.",
    "delete": "Delete",
    "saveError": "There was an error saving the rule, try again later.",
    "saveDelay": "This process may take a while. Please hold on until it finishes."
  },
  "es": {
    "saleRateIndexingRule": "Relacionar tarifas",
    "addRule": "Añadir regla de relación de tarifas",
    "deleteTitle": "¿Estás seguro de que quieres borrar esta regla de relación de tarifas?",
    "deleteError": "Ha habido un error borrando la regla de relación de tarifas",
    "createError": "Ha habido un error creando la regla de relación de tarifas",
    "noRules": "No hay reglas disponibles",
    "duplicateError": "La tarifa objetivo ya tiene una regla aplicada",
    "delete": "Borrar",
    "saveError": "Hubo un error actualizando la regla de indexación, inténtelo de nuevo más tarde.",
    "saveDelay": "Este proceso puede tardar un rato. Por favor, espere a que termine."
  }
}
</i18n>

<template>
  <v-container class="mb-10">
    <div class="d-flex justify-content-center">
      <div class="ml-auto text-center font-weight-bold ma-1 text-md-h6 text-body-1">
        {{ $t('saleRateIndexingRule') }}
      </div>
      <div class="ml-auto">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon class="mr-2" v-on="on" v-bind="attrs" @click="openCreateModal">
              <v-icon color="grey" large>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('addRule') }}</span>
        </v-tooltip>
      </div>
    </div>
    <dynameat-skeleton v-if="isLoading" skeleton-type="manualConfigList" :container="true"/>
    <div v-if="emptyRuleList && !isLoading" class="d-flex justify-space-around grey--text mt-5">
      <span>{{ $t('noRules') }}</span>
    </div>
    <v-list v-for="rule in saleRateIndexingRuleList" :key="rule.guid">
      <sale-rate-indexing-rule-card :rule="rule" @delete="onOpenDeleteModal(rule)" @edit="onEdit"/>
    </v-list>
    <sale-rate-indexing-rule-modal :visible="createModalVisible" @confirm="onConfirmCreateModal"
                                   @cancel="onCancelCreateModal"/>
    <dynameat-confirmation-modal :visible="deleteModalVisible" :title="$t('deleteTitle')"
                                 :description="deleteName" :confirm-text="$t('delete')"
                                 @confirm="onConfirmDeleteModal" @cancel="onCloseDeleteModal"/>
  </v-container>
</template>

<script>

import Vue from 'vue';
import Component from 'vue-class-component';
import { createErrorNotification, createWarningNotification } from '@ulibs/unnotificationsqueue';
import { MapActions, MapGetters } from '@core/helpers/decorators';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import SaleRateIndexingRuleCard from './components/SaleRateIndexingRuleCard.vue';
import SaleRateIndexingRuleModal from './components/SaleRateIndexingRuleModal.vue';

@Component({
  components: {
    SaleRateIndexingRuleModal, SaleRateIndexingRuleCard, DynameatConfirmationModal, DynameatSkeleton
  }
})
@MapActions(['saleRateIndexingRuleListRequest', 'createSaleRateIndexingRuleRequest',
  'removeSaleRateIndexingRuleRequest', 'setLoading', 'addNotification',
  'updateSaleRateIndexingRuleRequest'])
@MapGetters(['saleRateIndexingRuleList', 'isLoading', 'saleRateList'])
export default class SaleRateIndexingRule extends Vue {
  createModalVisible = false;

  deleteModalVisible = false;

  ruleToDelete = null;

  get deleteName() {
    if (this.ruleToDelete) {
      const referenceRateName = this.saleRateList.find((el) => el.guid === this.ruleToDelete.referenceSaleRateGuid)?.name;
      const changedRateName = this.saleRateList.find((el) => el.guid === this.ruleToDelete.changedSaleRateGuid)?.name;
      const sum = (this.ruleToDelete.fixedIncrement || this.ruleToDelete.percentageIncrement) > 0 ? '+' : '';
      const incrementToShow = this.ruleToDelete.fixedIncrement !== null ? `${this.ruleToDelete.fixedIncrement}€` : `${this.ruleToDelete.percentageIncrement}%`;
      return `${referenceRateName} -> ${changedRateName} (${sum}${incrementToShow})`;
    }
    return '';
  }

  get emptyRuleList() {
    return this.saleRateIndexingRuleList.length < 1;
  }

  created() {
    this.getSaleRateRuleList();
  }

  async getSaleRateRuleList() {
    try {
      this.setLoading(1);
      await this.saleRateIndexingRuleListRequest(this.$route.params.pointOfSaleGuid);
    } catch {
      this.addNotification(createErrorNotification(this.$t('requestError')));
    } finally {
      this.setLoading(-1);
    }
  }

  async onEdit(rule) {
    try {
      await this.updateSaleRateIndexingRuleRequest(rule);
      this.addNotification(createWarningNotification(this.$t('saveDelay')));
    } catch (err) {
      this.addNotification(createErrorNotification(this.$t('saveError'), null));
    }
  }

  openCreateModal() {
    this.createModalVisible = true;
  }

  onCancelCreateModal() {
    this.createModalVisible = false;
  }

  async onConfirmCreateModal(rule) {
    try {
      await this.setLoading(1);
      this.createModalVisible = false;
      this.addNotification(createWarningNotification(this.$t('saveDelay')));
      await this.createSaleRateIndexingRuleRequest(rule);
    } catch (err) {
      if (err.response.status) {
        this.addNotification(createErrorNotification(this.$t('duplicateError'), null));
      } else {
        this.addNotification(createErrorNotification(this.$t('createError'), null));
      }
    } finally {
      await this.setLoading(-1);
    }
  }

  onOpenDeleteModal(rule) {
    this.ruleToDelete = rule;
    this.deleteModalVisible = true;
  }

  onCloseDeleteModal() {
    this.ruleToDelete = null;
    this.deleteModalVisible = false;
  }

  async onConfirmDeleteModal() {
    this.deleteModalVisible = false;
    try {
      await this.setLoading(1);
      await this.removeSaleRateIndexingRuleRequest(this.ruleToDelete.guid);
    } catch (err) {
      this.addNotification(createErrorNotification(this.$t('deleteError'), null));
    } finally {
      await this.setLoading(-1);
    }
  }
}

</script>
